import React from 'react';

import { type MenuOption } from 'generated/graphql';
import { formatPrice } from 'utils';
import Image from 'components/Image/Image';

import useScreenWidthQuery from 'common/Hooks/UseScreenWidthQuery';
import styles from './Details.module.scss';
import { WindowBreakpoints } from '../../utils/DocumentHelper';

interface Props {
  optionGroup?: MenuOption | undefined | null;
}

const OptionGroupItem: React.FC<Props> = ({ optionGroup }) => {
  const [innerWidth] = useScreenWidthQuery();

  if (!optionGroup) {
    return null;
  }

  const { posId, variants, max, min } = optionGroup;

  const isDesktop = innerWidth > WindowBreakpoints.SM;

  const optionParagraph: string = (() => {
    if (min && max && min !== max) {
      return `Customers are required to select between ${min} and ${max} options.`;
    }
    if (min && max && min === max) {
      const optionText = `option${min > 1 ? 's' : ''}`;
      return `Customers are required to select at least ${min} ${optionText}.`;
    }
    if (max) {
      const optionText = `option${max > 1 ? 's' : ''}`;
      return `Customers can select a maximum of ${max} ${optionText}.`;
    }
    if (min) {
      const optionText = `option${min > 1 ? 's' : ''}`;
      return `Customers are required to select at least ${min} ${optionText}.`;
    }
    return '';
  })();

  const groupHasVaryingVariablesByApp = variants.some((variant) => {
    const { variablesByApp } = variant;
    const firstPrice = variablesByApp[0].price;
    return variablesByApp.some((item) => item.price !== firstPrice);
  });

  const lessThanFiveApps = !!(variants.length && variants[0].variablesByApp.length < 5);

  const topIcons =
    lessThanFiveApps && groupHasVaryingVariablesByApp && isDesktop
      ? variants[0].variablesByApp.map(({ app: { image, name } }) => {
          return { image, name };
        })
      : [];

  const useSimpleLayout = isDesktop && (lessThanFiveApps || !groupHasVaryingVariablesByApp);

  return (
    <div className={styles.OptionGroup} data-testid="optionGroup-item">
      {optionParagraph && <p data-testid="optionGroup-paragraph">{optionParagraph}</p>}
      {topIcons.length ? (
        <div className={styles.Icons} data-testid="optionGroup-icons">
          <ul>
            {topIcons.map(({ image, name }) => {
              return <li key={name}>{image && <Image className={styles.Icon} src={image} alt={name} />}</li>;
            })}
          </ul>
        </div>
      ) : null}
      {variants && (
        <ul>
          {variants.map((variant) => {
            const { posId: _posId, name: _name, variablesByApp } = variant;

            return (
              <li
                data-testid="optionGroup-row"
                key={_posId}
                className={!groupHasVaryingVariablesByApp ? styles.InlineOnMobile : ''}
              >
                <div className={styles.NameAndID}>{_name}</div>
                <ul
                  className={`${styles.PriceGrid} ${useSimpleLayout ? styles.SimpleLayout : ''}`}
                  data-testid="optionGroup-priceGrid"
                >
                  {variablesByApp &&
                    variablesByApp.map((item, index) => {
                      if (!groupHasVaryingVariablesByApp && index > 0) return null;
                      const { app, price } = item;

                      const formattedPrice = formatPrice(price);

                      return (
                        <li key={app.name} className={styles.Price}>
                          {app.image && !topIcons.length && groupHasVaryingVariablesByApp && (
                            <Image className={styles.Icon} src={app.image} alt={app.name} />
                          )}
                          <span className={formattedPrice.toLowerCase().includes('invalid') ? styles.Invalid : ''}>
                            {formattedPrice}
                          </span>
                        </li>
                      );
                    })}
                </ul>
              </li>
            );
          })}
        </ul>
      )}
      <div className={styles.PosID}>POS ID {posId}</div>
    </div>
  );
};

export default OptionGroupItem;
